import { useEffect, useCallback, useRef } from 'react';
import { useSubscription } from '@apollo/client/react';
import { REALTIME_SUBSCRIPTION } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';

interface SubscriptionEvent {
  subscribe: {
    type: string;
    _id: string;
    mutation: 'ADD' | 'UPDATE' | 'DELETE';
    subMutation?: string;
    teamId?: string;
    subType?: string;
    engage?: boolean;
  };
}

interface SubscriptionOptions {
  /** Debounce interval in ms (default 500) */
  debounceMs?: number;
  /**
   * If true, also refetch on UPDATE events (not just ADD/DELETE).
   * Use for collaborative editing scenarios where remote updates must be visible immediately.
   * Default: false (for scalability with large collections)
   * Ignored when batchFetchByIds is provided (batch fetch handles UPDATEs instead).
   */
  refetchOnUpdate?: boolean;
  /**
   * Batch fetch callback for remote UPDATE events (Notion-style notify-then-fetch).
   * When provided, UPDATE events collect entity IDs in a dedup Set during a 500ms
   * batch window, then call this function with all collected IDs at once.
   * The callback should query entities by IDs and let Apollo auto-merge into cache.
   */
  batchFetchByIds?: (ids: string[]) => Promise<void>;
  /**
   * Filter by event subType (case-insensitive match).
   * E.g., GROUP events include subType matching GROUP_TYPES ('device', 'asset', 'playlist', 'schedule', 'engage').
   * When set, only events matching this subType trigger refetch.
   */
  subType?: string;
}

/**
 * Subscribes to real-time changes for a specific entity type via WebSocket.
 *
 * Three event handling strategies:
 * - **ADD/DELETE**: Debounced full list refetch (structural changes)
 * - **UPDATE with batchFetchByIds**: Batch collect IDs in 500ms window, then
 *   multi-fetch changed entities by ID. Apollo auto-merges into list cache.
 * - **UPDATE without batchFetchByIds**: Ignored (local cache.modify handles own
 *   edits, focus/visibility fallback catches remote changes eventually)
 *
 * Falls back to window-focus/visibility refetch when WS is unavailable.
 *
 * @param entityType - The OBJECT_TYPES value to filter (e.g., 'device', 'asset')
 * @param refetch - Callback to refresh the list query
 * @param options - Optional configuration
 */
export function useRealtimeSubscription(
  entityType: string,
  refetch: () => void,
  options: SubscriptionOptions = {},
) {
  const { debounceMs = 500, refetchOnUpdate = false, subType } = options;
  const { activeTeamId } = useTeamStore();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastRefetchRef = useRef(0);
  // Store options in refs to avoid dependency issues
  const refetchOnUpdateRef = useRef(refetchOnUpdate);
  refetchOnUpdateRef.current = refetchOnUpdate;
  const batchFetchByIdsRef = useRef(options.batchFetchByIds);
  batchFetchByIdsRef.current = options.batchFetchByIds;
  const subTypeRef = useRef(subType);
  subTypeRef.current = subType;

  // Batch UPDATE state: dedup Set + timer
  const pendingUpdateIdsRef = useRef(new Set<string>());
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const debouncedRefetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      lastRefetchRef.current = Date.now();
      refetch();
    }, debounceMs);
  }, [refetch, debounceMs]);

  // Flush pending UPDATE IDs — fetch all collected entities in one batch
  const flushBatchUpdate = useCallback(() => {
    const ids = Array.from(pendingUpdateIdsRef.current);
    pendingUpdateIdsRef.current.clear();
    if (ids.length === 0) return;
    batchFetchByIdsRef.current?.(ids).catch(() => {
      // Fetch failed — fall back to full refetch
      refetch();
    });
  }, [refetch]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    };
  }, []);

  // WebSocket subscription — skip if no team selected
  const { error } = useSubscription<SubscriptionEvent>(REALTIME_SUBSCRIPTION, {
    variables: { teamId: activeTeamId },
    skip: !activeTeamId,
    onData: ({ data }) => {
      const event = data.data?.subscribe;
      if (!event) return;

      // Broadcast raw event globally so any listener (e.g. AuthProvider) can react
      // to event types without its own subscription. Apollo Client deduplicates
      // useSubscription hooks with identical query+variables, so not all onData
      // callbacks fire reliably — this ensures all event types are accessible.
      window.dispatchEvent(new CustomEvent('realtime:event', { detail: event }));

      // Filter by entity type (case-insensitive match)
      if (event.type?.toLowerCase() !== entityType.toLowerCase()) return;

      // Filter by subType if specified (e.g., GROUP events filtered by 'device', 'asset', etc.)
      if (subTypeRef.current && event.subType?.toLowerCase() !== subTypeRef.current.toLowerCase())
        return;

      if (event.mutation === 'UPDATE') {
        // MOVE is a structural change (item changes folder) — full list refetch like ADD/DELETE
        if (event.subMutation === 'MOVE') {
          debouncedRefetch();
          return;
        }
        // Strategy 1: Batch multi-fetch by IDs (preferred — Notion-style)
        if (batchFetchByIdsRef.current) {
          pendingUpdateIdsRef.current.add(event._id);
          if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
          batchTimerRef.current = setTimeout(flushBatchUpdate, debounceMs);
          return;
        }
        // Strategy 2: Full refetch on update (opt-in legacy mode)
        if (!refetchOnUpdateRef.current) return;
      }

      // ADD/DELETE or refetchOnUpdate=true: debounced full list refetch
      debouncedRefetch();
    },
  });

  // Safety net for Apollo subscription deduplication.
  // When multiple useSubscription hooks share identical query+variables (e.g., entity + group
  // subscriptions on the same page), Apollo may only fire one onData callback reliably.
  // The broadcast at line 121 dispatches all events globally — this listener catches GROUP
  // events that the deduped onData may have missed, ensuring folder security changes
  // (e.g., isFolderSecurityEdit toggled) trigger a refetch of the current group.
  useEffect(() => {
    if (entityType.toLowerCase() !== 'group') return;

    const handleBroadcast = (e: Event) => {
      const event = (e as CustomEvent).detail;
      if (!event || event.type?.toLowerCase() !== 'group') return;
      if (subTypeRef.current && event.subType?.toLowerCase() !== subTypeRef.current.toLowerCase())
        return;

      if (event.mutation === 'UPDATE') {
        if (event.subMutation === 'MOVE') {
          debouncedRefetch();
          return;
        }
        if (!refetchOnUpdateRef.current) return;
      }
      debouncedRefetch();
    };

    window.addEventListener('realtime:event', handleBroadcast);
    return () => window.removeEventListener('realtime:event', handleBroadcast);
  }, [entityType, debouncedRefetch]);

  // Fallback: refetch on window focus/visibility ONLY when WS is down.
  // When WS is healthy (!error), events arrive via subscription — focus refetch is redundant.
  // When WS is down (error truthy), focus refetch is the only way to catch missed events,
  // so we keep the bare listener active (no blur guard — user may be active on page).
  const errorRef = useRef(error);
  errorRef.current = error;
  useEffect(() => {
    const FOCUS_DEBOUNCE_MS = 2_000;

    const handleFocus = () => {
      if (!errorRef.current) return; // WS healthy, subscription handles events
      const now = Date.now();
      if (now - lastRefetchRef.current > FOCUS_DEBOUNCE_MS) {
        lastRefetchRef.current = now;
        refetch();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refetch]);

  // Log WS errors in dev but don't crash
  useEffect(() => {
    if (error) {
      console.warn(
        '[RealtimeSubscription] WebSocket error, falling back to focus-based refresh:',
        error.message,
      );
    }
  }, [error]);
}
